import { Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { company } from '../content/company';
import { products } from '../content/products';
import { Logo } from './Brand';
import { Loader } from './Loader';

const nav = [
  { to: '/products', label: 'Products' },
  { to: '/company', label: 'Company' },
  { to: '/contact', label: 'Contact' },
];

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView();
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function Header() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <header className="header">
      <div className="container header__inner">
        <Link to="/" className="header__home" aria-label={`${company.legalName}, home`} onClick={close}>
          <Logo />
        </Link>
        <button
          type="button"
          className="header__toggle"
          aria-expanded={open}
          aria-controls="site-nav"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="sr-only">{open ? 'Close menu' : 'Open menu'}</span>
          <span className={`burger${open ? ' burger--open' : ''}`} aria-hidden="true" />
        </button>
        <nav id="site-nav" className={`nav${open ? ' nav--open' : ''}`} aria-label="Main">
          {nav.map((item) => (
            <NavLink key={item.to} to={item.to} className="nav__link" onClick={close}>
              {item.label}
            </NavLink>
          ))}
          <a className="btn btn--primary btn--sm nav__cta" href={`mailto:${company.email}`} onClick={close}>
            Get in touch
          </a>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__grid">
          <div className="footer__brand">
            <Logo inverse />
            <p className="footer__tagline">Global Innovative Minds</p>
            <p className="footer__lede">
              Secure digital products for verification, phone security and professional connection.
            </p>
          </div>
          <div>
            <h2 className="footer__head">Products</h2>
            <ul className="footer__list">
              {products.map((p) => (
                <li key={p.id}>
                  <Link to={`/products#${p.id}`}>{p.name}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="footer__head">Company</h2>
            <ul className="footer__list">
              <li>
                <Link to="/company">About us</Link>
              </li>
              <li>
                <Link to="/company#information">Company information</Link>
              </li>
              <li>
                <Link to="/contact">Contact</Link>
              </li>
            </ul>
          </div>
          <div>
            <h2 className="footer__head">Contact</h2>
            <address className="footer__address">
              {company.address.lines.map((l) => (
                <span key={l}>{l}</span>
              ))}
              <span>{company.address.country}</span>
              <a href={`mailto:${company.email}`}>{company.email}</a>
              <a href={company.phoneHref}>{company.phoneDisplay}</a>
            </address>
          </div>
        </div>

        <div className="footer__legal">
          <p>
            © {new Date().getFullYear()} {company.legalName}. A {company.entityType.toLowerCase()} incorporated in
            the {company.jurisdiction} under the {company.incorporationAct}, company number{' '}
            {company.registrationNumber}.
          </p>
          <ul className="footer__legal-links">
            <li>
              <Link to="/privacy">Privacy policy</Link>
            </li>
            <li>
              <Link to="/terms">Terms of use</Link>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}

export function Layout() {
  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <ScrollManager />
      <Header />
      <main id="main" tabIndex={-1}>
        <Suspense fallback={<Loader />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
    </>
  );
}
